import { ximiMobileGetFlat } from './ximiMobileClient';

// Endpoint Ozmo (lecture seule) : renvoie un JWT RS256 signé décrivant l'identité Ximi,
// que StimulinAPI échangera ensuite contre ses propres tokens. Voir
// .claude/migration/06-provisioning-user-depuis-ximi.md
const STIMULIN_IDENTITY = 'stimulin/identity';

/**
 * Codes d'erreur métier renvoyés par `api/stimulin/identity` (Ozmo), miroir du contrat côté
 * VM — à garder synchronisé. Ils distinguent un refus explicable d'une panne : sans eux, tous
 * les échecs se ressemblent et l'utilisateur ne sait pas quoi faire.
 */
export const XimiIdentityErrorCode = {
    /** Compte Ximi sans adresse e-mail : authentifié, mais non provisionnable. */
    EMAIL_MISSING: 'STIMULIN_EMAIL_MISSING',
    /** Le tenant n'a pas souscrit au service Stimul'In. */
    SUBSCRIPTION_INACTIVE: 'STIMULIN_SUBSCRIPTION_INACTIVE'
    // NB : STIMULIN_KIND_NOT_ALLOWED existe côté Ozmo (route beneficiaries, kinds futurs non
    // gérés) mais ne remonte jamais à l'écran de login — l'identité sert les trois kinds.
} as const;

export type XimiIdentityErrorCode =
    (typeof XimiIdentityErrorCode)[keyof typeof XimiIdentityErrorCode];

/** Claims du JWT d'identité Stimul'In (api/stimulin/identity). */
export interface XimiStimulinIdentityClaims {
    tenant?: string;
    appId?: string;
    kind?: 'Client' | 'ClientHelper' | 'Agent';
    id?: number;
    email?: string;
    firstName?: string;
    lastName?: string;
    dob?: string | null;
    agencyId?: number; // Client & Agent uniquement
    agencyName?: string; // Client & Agent uniquement
    jti?: string;
    iss?: string;
    iat?: number;
    exp?: number;
    [key: string]: unknown;
}

export type XimiStimulinIdentityResult =
    | { ok: true; status: number; token: string; claims: XimiStimulinIdentityClaims }
    | { ok: false; status?: number; code?: string; reason: string };

/** Décode le payload d'un JWT (base64url, UTF-8) SANS vérifier la signature — lecture seule. */
function decodeJwtPayload(token: string): XimiStimulinIdentityClaims {
    const payload = token.split('.')[1];
    if (!payload) throw new Error('Malformed JWT');
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
        atob(base64)
            .split('')
            .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
            .join('')
    );
    return JSON.parse(json) as XimiStimulinIdentityClaims;
}

/** Appelle api/stimulin/identity avec le Bearer SSO courant ; renvoie le JWT + ses claims. */
export async function fetchXimiStimulinIdentity(): Promise<XimiStimulinIdentityResult> {
    try {
        const { status, data } = await ximiMobileGetFlat<{ token?: string; code?: string }>(
            STIMULIN_IDENTITY
        );
        // Le code métier vit dans le corps : le jeter rendrait tous les refus indiscernables.
        if (status < 200 || status >= 300) {
            return { ok: false, status, code: data?.code, reason: `HTTP ${status}` };
        }
        const token = data?.token;
        if (!token) return { ok: false, status, reason: 'No token in response' };
        return { ok: true, status, token, claims: decodeJwtPayload(token) };
    } catch (error) {
        return { ok: false, reason: error instanceof Error ? error.message : 'Network error' };
    }
}
